import React, { isValidElement, ReactElement } from 'react';
import { isAndroid, isUMUWebview } from '@umu/umu-ua';
import { IAppShareOption } from '@umu/umu-types';
import { lang, getCurrentSystem, getCurrentLang } from '@umu/ukit-lang';
import WebView, {
    APP_ICON_FONT,
    ENavBarType,
    ENavItemTextButtonStyle,
    ENavItemContainedButtonStyle,
    getAppIconFontUrl,
    INavigationBarDestroy,
    INavigationIconFontBarItem,
    INavigationContainedButtonBarItem,
    INavigationBarItem,
    INavigationTextButtonBarItem,
} from '@umu/umu-webview';

function getId() {
    return Date.now() + '_ms';
}

interface INavAction {
    title?: string;
    enabled?: boolean;
    link?: string;
    shareOptions?: IAppShareOption;
    actionName: string;
    actionFn: Function;
    style?: ENavItemTextButtonStyle;
}

export enum ENavItemActionType {
    /**
     * 导航栏[返回]图标
     */
    ICON_BACK = 'back',

    /**
     * 导航栏[链接]图标
     */
    ICON_LINK = 'link',

    /**
     * 导航栏[设置]图标
     */
    ICON_SETTING = 'setting',

    /**
     * 导航栏[关闭]图标
     */
    ICON_CLOSE = 'close',

    /**
     * 导航栏[新建]图标
     */
    ICON_CREATE = 'create',

    /**
     * 导航栏[分享]图标
     */
    ICON_SHARE = 'share',

    /**
     * 导航栏[返回]图标
     */
    ICON_CUSTOMIZE_BACK = 'customize_back',

    /**
     * 导航栏
     */
    TEXT = 'text',
    TEXT_BACK = 'text_back',
    ACTION = 'action',
    ACTION_BUTTON = 'action_button',
}

interface INavItems {
    action: ENavItemActionType;
    data: INavAction;
}

export type INativeNavHeaderProps = {
    title?: string;
    webViewLeftItems?: INavItems[];
    webViewRightItems?: INavItems[];
};

const defaultBackTextNavBarItem = {
    action: ENavItemActionType.TEXT_BACK,
    data: {
        title: lang('取消'),
        actionName: 'goBack',
        actionFn: WebView.api.popBack,
    },
};

export const defaultBackTextNavBarItems = [defaultBackTextNavBarItem];

export const getNativeActionIconBarItem = ({ actionName, actionFn }: { actionName: string; actionFn: Function }) => {
    return {
        action: ENavItemActionType.ICON_CUSTOMIZE_BACK,
        data: {
            actionName,
            actionFn,
        },
    };
};

export const getNativeActionShareBarItem = ({ shareOptions }: { shareOptions: IAppShareOption }) => {
    return {
        action: ENavItemActionType.ICON_SHARE,
        data: {
            shareOptions,
            actionName: 'callShare' + getId(),
            actionFn: () => {
                WebView.callHandler(
                    'callShare',
                    shareOptions,
                    () => {
                        //
                    },
                    LANG,
                );
            },
        },
    };
};

export const getNativeActionSettingItem = ({ link }: { link: string }) => {
    return getNativeIconLinkBarItem({
        link,
        actionName: ENavItemActionType.ICON_SETTING,
    });
};

export const getNativeActionAddItem = ({ link }: { link: string }) => {
    return getNativeIconLinkBarItem({
        link,
        actionName: ENavItemActionType.ICON_CREATE,
    });
};

const getNativeIconLinkBarItem = ({ link, actionName }: { link: string; actionName: ENavItemActionType }) => {
    return {
        action: actionName,
        data: {
            link,
            actionName: 'gotoLink' + getId(),
            actionFn: () => window.open(link),
        },
    };
};

export const getNativeActionTextBarItem = ({
    title,
    enabled = true,
    actionName,
    actionFn,
    style = ENavItemTextButtonStyle.BigSub,
}: {
    title: string;
    enabled?: boolean;
    actionName: string;
    actionFn: Function;
    style?: ENavItemTextButtonStyle;
}) => {
    return {
        action: ENavItemActionType.ACTION,
        data: {
            title,
            enabled,
            actionName,
            actionFn,
            style,
        },
    };
};

export const getNativeActionButtonBarItem = ({
    title,
    enabled = true,
    actionName,
    actionFn,
    style = ENavItemTextButtonStyle.BigSub,
}: {
    title: string;
    enabled?: boolean;
    actionName: string;
    actionFn: Function;
    style?: ENavItemTextButtonStyle;
}) => {
    return {
        action: ENavItemActionType.ACTION_BUTTON,
        data: {
            title,
            enabled,
            actionName,
            actionFn,
            style,
        },
    };
};

export const defaultBackNavBarItem = {
    action: ENavItemActionType.ICON_BACK,
    data: {
        actionName: 'popBack',
        actionFn: WebView.api.popBack,
    },
};
export const defaultNativeNavBarLeftItems = [defaultBackNavBarItem];

export const LANG = getCurrentLang().toUpperCase() as
    | 'CN'
    | 'EN'
    | 'ES'
    | 'TH'
    | 'JP'
    | 'TW'
    | 'KO'
    | 'FR'
    | 'ID'
    | 'RU'
    | 'PT'
    | 'DE'
    | 'IT'
    | 'VI'
    | undefined;

function getNavBarTextItem(actionType: ENavItemActionType, data: INavAction): INavigationTextButtonBarItem {
    const { title, enabled = true, actionName } = data;
    let { style = ENavItemTextButtonStyle.BigSub } = data;
    if (actionType === ENavItemActionType.TEXT_BACK) {
        style = ENavItemTextButtonStyle.BigGrey;
    } else if (actionType === ENavItemActionType.ACTION) {
        style = data.style || ENavItemTextButtonStyle.BigSub;
    }

    return {
        id: getId(),
        type: ENavBarType.NavigationTextButtonBarItem,
        config: {
            text: title!,
            enable: enabled,
            action: actionName,
            style: style!,
        },
    };
}

function getNavButtonBarItem(_actionType: ENavItemActionType, data: INavAction): INavigationContainedButtonBarItem {
    const { title, enabled = true, actionName } = data;
    return {
        id: getId(),
        type: ENavBarType.NavigationContainedButtonBarItem,
        config: {
            text: title!,
            enable: enabled,
            action: actionName,
            style: ENavItemContainedButtonStyle.SmallBrand,
        },
    };
}

function getNavBarIconItem(actionType: ENavItemActionType, data: INavAction): INavigationIconFontBarItem {
    let iconName = APP_ICON_FONT.NavCreate;
    if (actionType === ENavItemActionType.ICON_LINK) {
        iconName = APP_ICON_FONT.Link;
    } else if (actionType === ENavItemActionType.ICON_SETTING) {
        iconName = APP_ICON_FONT.NavSettings;
    } else if (actionType === ENavItemActionType.ICON_CREATE) {
        iconName = APP_ICON_FONT.NavCreate;
    } else if (actionType === ENavItemActionType.ICON_SHARE) {
        iconName = APP_ICON_FONT.NavShare;
    } else if (actionType === ENavItemActionType.ICON_CLOSE) {
        iconName = APP_ICON_FONT.NavClose;
    } else if (actionType === ENavItemActionType.ICON_BACK || actionType === ENavItemActionType.ICON_CUSTOMIZE_BACK) {
        iconName = isAndroid ? APP_ICON_FONT.AndroidBack : APP_ICON_FONT.NavBack;
    }

    const { actionName } = data;

    return {
        id: getId(),
        type: ENavBarType.NavigationIconFontBarItem,
        config: {
            icon_name: iconName,
            enable: true,
            action: actionName,
            image_url: getAppIconFontUrl(iconName, getCurrentSystem()),
        },
    };
}

function getTitleLabel(title?: string) {
    if (!title) {
        return undefined;
    }

    return {
        id: getId(),
        type: ENavBarType.Label,
        config: {
            text: title,
            text_color: '#212121',
            font_size: '18',
        },
    };
}

export const isNativeHybridActionEnabled = WebView.isVersionLargerThan('6.7.8') && isUMUWebview;

const nameActionMap: Record<string, Function> = {};

export class BaseNavHeader<T extends INativeNavHeaderProps> extends React.Component<T, {}> {
    webViewLink: string;
    renderObj: INavigationBarDestroy;

    constructor(props: T) {
        super(props);

        this.webViewLink = '';
        this.renderObj = {} as INavigationBarDestroy;

        if (!this.isShowWebView()) {
            return;
        }

        this.registerWebview();
    }

    protected isShowWebView() {
        const isNativeHeaderEnabled = WebView.isVersionLargerThan('6.7.8');

        return isUMUWebview && isNativeHeaderEnabled;
    }

    private registerWebview() {
        WebView.registerHandler('handleMessageFromNative', (nativeMsg) => {
            try {
                const { namespace, method, data } = nativeMsg;
                if (namespace === 'core' && method === 'action') {
                    const actionName = data.action;
                    const actionFn = nameActionMap[actionName];
                    if (actionFn) {
                        actionFn();
                    }
                } else if (namespace === 'container_lifecycle' && method === 'urlChange') {
                    WebView.api.destroyRender(this.renderObj);
                }
            } catch (e) {
                alert('error: ' + JSON.stringify(nativeMsg));
            }
        });
    }

    private transformNavItem(action: ENavItemActionType, data: INavAction): INavigationBarItem {
        const { actionName, actionFn } = data;
        nameActionMap[actionName] = actionFn;
        if (
            action === ENavItemActionType.ICON_LINK ||
            action === ENavItemActionType.ICON_SETTING ||
            action === ENavItemActionType.ICON_CREATE ||
            action === ENavItemActionType.ICON_SHARE ||
            action === ENavItemActionType.ICON_BACK ||
            action === ENavItemActionType.ICON_CLOSE ||
            action === ENavItemActionType.ICON_CUSTOMIZE_BACK // 自定义数据内容
        ) {
            return getNavBarIconItem(action, data);
        }
        if (action === ENavItemActionType.TEXT_BACK || action === ENavItemActionType.ACTION) {
            return getNavBarTextItem(action, data);
        }
        if (action === ENavItemActionType.ACTION_BUTTON) {
            return getNavButtonBarItem(action, data);
        }

        throw new Error('not support');
    }

    private transformSingleNavBarItems(navBarItems?: INavItems[]): INavigationBarItem[] {
        if (!navBarItems || !navBarItems.length) {
            return [];
        }

        return navBarItems.map(({ action, data }) => this.transformNavItem(action, data));
    }

    private transformNavBarItems() {
        const left_bar_items = this.transformSingleNavBarItems(this.props.webViewLeftItems);
        const right_bar_items = this.transformSingleNavBarItems(this.props.webViewRightItems);
        return { left_bar_items, right_bar_items };
    }

    protected renderNavigationBar() {
        const { title: originTitle } = this.props;
        let navTitle = originTitle;
        const isTitleReactElement = isValidElement(originTitle);
        if (isTitleReactElement) {
            navTitle = (originTitle as ReactElement).props.title;
        } else {
            navTitle = originTitle;
        }
        const obj = {
            id: getId(),
            type: 'NavigationBar',
            config: {
                title_items: [getTitleLabel(navTitle)],
                left_bar_items: [] as INavigationBarItem[],
                right_bar_items: [] as INavigationBarItem[],
            },
        };

        this.renderObj = {
            id: obj.id,
            type: obj.type,
        };

        const { left_bar_items, right_bar_items } = this.transformNavBarItems();
        obj.config.left_bar_items = left_bar_items;
        obj.config.right_bar_items = right_bar_items;

        WebView.api.createRender(obj as any);
    }

    public componentWillUnmount(): void {
        WebView.api.destroyRender(this.renderObj);
    }

    render() {
        if (this.isShowWebView()) {
            this.renderNavigationBar();

            return null;
        }

        return <></>;
    }
}
